// 功能開關配置
// 用於控制新功能的啟用/禁用，確保線上版本穩定

export interface FeatureFlags {
  // Notebook相關功能
  enableNotebookFeatures: boolean;
  enableDocumentProcessing: boolean;
  enableAIAssistant: boolean;
  
  // 新AI功能
  enableNewAIFeatures: boolean;
  enableAdvancedChat: boolean;
  enableLanguageDetection: boolean;
  
  // 分析功能
  enableAnalytics: boolean;
  enableChatAnalytics: boolean;
  enableUserBehaviorTracking: boolean;
  
  // 調試功能
  enableDebugMode: boolean;
  enableConsoleLogging: boolean;
  
  // 實驗性功能
  enableExperimentalFeatures: boolean;
  enableBetaFeatures: boolean;
}

// 根據環境變量設置功能開關
const getFeatureFlags = (): FeatureFlags => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProduction = process.env.NODE_ENV === 'production';
  
  return {
    // Notebook相關功能 - 開發環境啟用，生產環境禁用
    enableNotebookFeatures: isDevelopment || process.env.REACT_APP_ENABLE_NOTEBOOK_FEATURES === 'true',
    enableDocumentProcessing: isDevelopment || process.env.REACT_APP_ENABLE_DOCUMENT_PROCESSING === 'true',
    enableAIAssistant: isDevelopment || process.env.REACT_APP_ENABLE_AI_ASSISTANT === 'true',
    
    // 新AI功能 - 根據環境變量控制
    enableNewAIFeatures: isDevelopment || process.env.REACT_APP_ENABLE_NEW_AI_FEATURES === 'true',
    enableAdvancedChat: isDevelopment || process.env.REACT_APP_ENABLE_ADVANCED_CHAT === 'true',
    enableLanguageDetection: true, // 已穩定，始終啟用
    
    // 分析功能 - 生產環境啟用
    enableAnalytics: isProduction || process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableChatAnalytics: isProduction || process.env.REACT_APP_ENABLE_CHAT_ANALYTICS === 'true',
    enableUserBehaviorTracking: isProduction || process.env.REACT_APP_ENABLE_USER_BEHAVIOR_TRACKING === 'true',
    
    // 調試功能 - 僅開發環境
    enableDebugMode: isDevelopment,
    enableConsoleLogging: isDevelopment,
    
    // 實驗性功能 - 僅開發環境
    enableExperimentalFeatures: isDevelopment,
    enableBetaFeatures: isDevelopment,
  };
};

export const featureFlags = getFeatureFlags();

// 功能檢查工具函數
export const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
  return featureFlags[feature];
};

// 條件渲染Hook
export const useFeatureFlag = (feature: keyof FeatureFlags): boolean => {
  return isFeatureEnabled(feature);
};

// 開發環境功能開關控制台
if (process.env.NODE_ENV === 'development') {
  console.log('🚀 Feature Flags:', featureFlags);
  
  // 在開發環境中將功能開關暴露到全局對象
  (window as any).featureFlags = featureFlags;
  (window as any).toggleFeature = (feature: keyof FeatureFlags) => {
    featureFlags[feature] = !featureFlags[feature];
    console.log(`🔄 Toggled ${feature}:`, featureFlags[feature]);
  };
}
